import React, { useMemo, useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Moment } from 'moment';
import { SimpleCheckbox } from '../../common/SimpleCheckbox/SimpleCheckbox';
import moment from 'moment';
import { Config } from '@/Config';
import { useTranslation } from 'react-i18next';
import TimePicker from 'react-time-picker';
import { parseMomentFromTime } from '@/utils/helper';

import './TimedReleaseDateTimePicker.scss';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-time-picker/dist/TimePicker.css';

interface Props {
  id?: string;
  className?: string;
  onDateChange: (date: Date | [Date, Date] | null) => void;
  onTimeChange: (newValue: Moment | undefined | null) => void;
  onFocus?: any;
  onBlur?: any;
  date?: Date;
  time?: Moment;
  label: string;
  labelClassName?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  calendarClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  onCalendarOpen?: () => void;
  onCalendarClose?: () => void;
}

const defaultProps = {
  value: undefined,
  id: '',
  className: '',
  autoComplete: 'off',
};

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

const DateTimePicker = ({
  id,
  labelClassName,
  onDateChange,
  onTimeChange,
  date,
  time,
  label,
  className,
  disabled,
  onFocus,
  onBlur,
  calendarClassName,
  minDate,
  maxDate,
  onCalendarOpen,
  onCalendarClose,
}: Props) => {
  const { t } = useTranslation();
  const [activateTimedRelease, setActivateTimedRelease] = useState(!!time);

  useEffect(() => {
    setActivateTimedRelease(!!time);
  }, [time]);

  const getFormattedDate = useMemo(() => {
    if (time && date) {
      const options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      };
      const formattedDate = new Intl.DateTimeFormat('en-US', options).format(
        date
      );
      return `${formattedDate} ${time.format('hh:mm a')} UTC`;
    }
    return '';
  }, [date, time]);

  const timeZoneConverterUrl = useMemo(() => {
    if (date && time) {
      const combinedMoment = moment(date)
        .hour(time.hour())
        .minute(time.minute())
        .second(time.second())
        .millisecond(time.millisecond());

      if (combinedMoment.isValid()) {
        const iso = combinedMoment.format('YYYYMMDDTHHmmss');
        const params = Config.TIMEZONE_CONVERTER_PARAMS
          ? Config.TIMEZONE_CONVERTER_PARAMS.replace(/,/g, '&') + `&iso=${iso}`
          : `iso=${iso}`;
        return `${Config.TIMEZONE_CONVERTER_LINK}?${params}`;
      }
    }
    return '';
  }, [date, time]);

  return (
    <>
      <div className="timed-release-row">
        <label className={labelClassName} htmlFor={label}>
          {label}
        </label>
        <ReactDatePicker
          id={id}
          selected={date ? moment(date).toDate() : null}
          onChange={(date: Date | [Date, Date] | null) => {
            if (!date) {
              onTimeChange(null);
              setActivateTimedRelease(false);
            }
            onDateChange(date);
          }}
          className={className}
          dateFormat={DEFAULT_DATE_FORMAT}
          dropdownMode="select"
          showIcon={!disabled}
          isClearable={!disabled}
          readOnly={disabled}
          onFocus={onFocus}
          onBlur={onBlur}
          icon="icon fas fa-calendar-alt" // Assuming you have font-awesome
          calendarIconClassname={calendarClassName}
          autoComplete="off"
          minDate={minDate}
          maxDate={maxDate}
          onCalendarClose={onCalendarClose}
          onCalendarOpen={onCalendarOpen}
        />
        {activateTimedRelease && time && (
          <TimePicker
            className={`time-picker ${className}`}
            format="hh:mm a"
            disableClock={true}
            disabled={disabled}
            onChange={(value) => onTimeChange(parseMomentFromTime(value))}
            value={time.format('HH:mm')}
          />
        )}

        <div className="timed-release-checkbox">
          <SimpleCheckbox
            text={t('product.editProduct.activateTimedPreorder')}
            checked={activateTimedRelease}
            onChange={(e) => {
              if (e.target.checked) {
                onTimeChange(moment().hour(0).minute(0));
              } else {
                onTimeChange(null);
              }
              setActivateTimedRelease(e.target.checked);
            }}
            disabled={!date || disabled}
          />
        </div>
      </div>
      {activateTimedRelease && date && time && (
        <div className="timed-release-row">
          <label className={labelClassName} htmlFor={label}></label>
          <label
            className={'item-text-right col-sm-6 textLink timezone-label'}
            htmlFor={label}
          >
            <a
              href={timeZoneConverterUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i
                className="icon fas fa-info-circle timezone-icon"
                aria-hidden="true"
              ></i>
              {t('product.editProduct.timezoneConvert', {
                timezoneUrl: getFormattedDate,
              })}
            </a>
          </label>
        </div>
      )}
    </>
  );
};

DateTimePicker.defaultProps = defaultProps;

export default DateTimePicker;
